import type { ColumnDef } from "@tanstack/react-table";
import type { ICotizacionNBS } from "../../../modelos/CotizadorNBS/entidades/ICotizacionNBS";
import { ActionIcon, Group, Text, Tooltip } from "@mantine/core";
import { IconDownload, IconEdit, IconEye, IconFileTypePdf, IconTrash } from "@tabler/icons-react";
import { EnumEstadoCotizacionNBS, puedeGenerarPdf, type EstadoCotizacionNBSId } from "../../../modelos/CotizadorNBS/entidades/EstadoCotizacionNBS";
import { BadgeEstadoCotizacionVista } from "./badgeEstadoCotizacionVista";

interface ColumnasTablaCotizacionesParams {
    manejarEdicionCotizacion?: (cotizacion: ICotizacionNBS) => void;
    manejarEliminacionCotizacion?: (cotizacion: ICotizacionNBS) => void;
    /** Cambio de estado desde la columna Estado (solo transiciones que envía el back). */
    manejarCambioEstado?: (cotizacion: ICotizacionNBS, estado: EstadoCotizacionNBSId) => void;
    /** PDF de la cotización (como en Gestión de archivos): previsualizar o descargar. Fuera de Borrador. */
    manejarPrevisualizarPdf?: (cotizacion: ICotizacionNBS) => void;
    manejarDescargarPdf?: (cotizacion: ICotizacionNBS) => void;
}

export const columnasTablaCotizaciones = ({
    manejarEdicionCotizacion,
    manejarEliminacionCotizacion,
    manejarCambioEstado,
    manejarPrevisualizarPdf,
    manejarDescargarPdf
}: ColumnasTablaCotizacionesParams = {}): ColumnDef<ICotizacionNBS>[] => [
        {
            id: "actions",
            header: "Acciones",
            cell: ({ row: fila }) => {
                const estadoId = fila.original.Estado?.Id as number | undefined;
                const esAceptada = estadoId === EnumEstadoCotizacionNBS.Aceptada;
                return (
                <Group gap={4} justify="center" wrap="nowrap">
                    {/* Solo Aceptada es de solo lectura: se abre para ver. */}
                    <Tooltip label={esAceptada ? "Ver" : "Editar"} style={{ fontSize: '10px', padding: '3px 5px' }} withArrow>
                        <ActionIcon
                            color='var(--mantine-color-primary-0)'
                            variant="transparent"
                            radius="sm"
                            size="sm"
                            onClick={() => manejarEdicionCotizacion && manejarEdicionCotizacion(fila.original)}
                        >
                            {esAceptada ? <IconEye size={18} /> : <IconEdit size={18} />}
                        </ActionIcon>
                    </Tooltip>
                    {puedeGenerarPdf(estadoId) && manejarPrevisualizarPdf && (
                    <Tooltip label="Previsualizar PDF" style={{ fontSize: '10px', padding: '3px 5px' }} withArrow>
                        <ActionIcon
                            color='var(--mantine-color-primary-0)'
                            variant="transparent"
                            radius="sm"
                            size="sm"
                            onClick={() => manejarPrevisualizarPdf(fila.original)}
                        >
                            <IconFileTypePdf size={18} />
                        </ActionIcon>
                    </Tooltip>
                    )}
                    {puedeGenerarPdf(estadoId) && manejarDescargarPdf && (
                    <Tooltip label="Descargar PDF" style={{ fontSize: '10px', padding: '3px 5px' }} withArrow>
                        <ActionIcon
                            color='var(--mantine-color-primary-0)'
                            variant="transparent"
                            radius="sm"
                            size="sm"
                            onClick={() => manejarDescargarPdf(fila.original)}
                        >
                            <IconDownload size={18} />
                        </ActionIcon>
                    </Tooltip>
                    )}
                    {/* Una cotización aceptada no se elimina (el back también lo impide). */}
                    {!esAceptada && (
                    <Tooltip style={{ fontSize: '10px', padding: '3px 5px' }} label="Eliminar" withArrow>
                        <ActionIcon
                            color="var(--mantine-color-primary-0)"
                            variant="transparent"
                            radius="sm"
                            size="sm"
                            onClick={() => manejarEliminacionCotizacion && manejarEliminacionCotizacion(fila.original)}
                        >
                            <IconTrash size={18} />
                        </ActionIcon>
                    </Tooltip>
                    )}
                </Group>
                );
            },
        },
        {
            accessorKey: "Codigo",
            header: "Código",
            cell: ({ getValue }) => <Text size="sm" fw={500}>{(getValue() as string) || '-'}</Text>,
        },
        {
            accessorKey: "FechaCreacion",
            header: "Fecha Creación",
            cell: ({ getValue }) => {
                const fecha = getValue() as string;
                return (
                    <Text size="sm">
                        {fecha ? new Date(fecha).toLocaleDateString('es-CO') : '-'}
                    </Text>
                );
            },
        },
        {
            accessorKey: "NombreCliente",
            header: "Cliente",
        },
        {
            accessorKey: "NombreProyecto",
            header: "Proyecto",
        },
        {
            accessorKey: "Ciudad",
            header: "Ciudad",
        },
        {
            accessorKey: "PrecioVentaTotal",
            // La moneda va en el valor, no en el título de la columna (igual que en el Resumen).
            header: "Precio Venta",
            cell: ({ getValue }) => {
                const valor = getValue() as number;
                if (!valor) return <Text size="sm">-</Text>;
                return (
                    <Text size="sm" fw={500}>
                        $ {valor.toLocaleString('es-CO')} USD
                    </Text>
                );
            },
        },
        {
            accessorKey: "TotalEquipos",
            header: "Equipos",
            // Unidades por tipo: "3 Equipos (2 Ascensores y 1 Escalera)".
            cell: ({ getValue, row }) => {
                const total = getValue() as number || 0;
                const cuenta = (n: number, singular: string, plural: string) => `${n} ${n === 1 ? singular : plural}`;
                const detalle = [
                    row.original.TotalAscensores ? cuenta(row.original.TotalAscensores, 'Ascensor', 'Ascensores') : null,
                    row.original.TotalEscaleras ? cuenta(row.original.TotalEscaleras, 'Escalera', 'Escaleras') : null,
                ].filter(Boolean).join(' y ');
                return (
                    <Text size="sm">
                        {cuenta(total, 'Equipo', 'Equipos')}
                        {detalle && <Text span size="sm" c="dimmed"> ({detalle})</Text>}
                    </Text>
                );
            },
        },
        {
            accessorKey: "UsuarioCreadorNombre",
            header: "Usuario Creación",
            cell: ({ getValue }) => <Text size="sm">{getValue() as string || '-'}</Text>
        },
        {
            accessorKey: "Pais",
            header: "País",
            cell: ({ getValue }) => <Text size="sm">{getValue() as string || '-'}</Text>
        },
        {
            accessorKey: "Estado",
            header: "Estado",
            // Clic en la etiqueta: menú con los estados a los que se puede pasar.
            cell: ({ row: fila }) => (
                <BadgeEstadoCotizacionVista
                    estadoId={fila.original.Estado?.Id as number | undefined}
                    transiciones={fila.original.TransicionesPermitidas}
                    alCambiarEstado={manejarCambioEstado ? (estado) => manejarCambioEstado(fila.original, estado) : undefined}
                />
            ),
        },

    ];
